/**
 * CortexBranchesPulse — telemetria em tempo real dos 3 branches do Cortex
 * Hardening: banter (zoeira coletiva), reaffirmation (loop de fato repetido)
 * e admin directive (comando administrativo aplicado).
 *
 * Mostra contadores das últimas 24h + delta vs 24h anteriores. Atualiza:
 *   - via Supabase Realtime (INSERT em qualquer das 3 tabelas)
 *   - fallback poll a cada 30s
 *
 * Tabelas:
 *   - banter_detections        (banter_score, downgraded_intent_*)
 *   - syra_messages            (filtra anti-repetition activations via cortex_events)
 *     → na prática lemos cortex_events.event_type='reaffirmation.blocked'
 *   - admin_directives_log     (applied_action != denied/error)
 */
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import {
  Laugh, RotateCcw, ShieldCheck, ArrowRight, TrendingUp, TrendingDown,
  Minus, Activity,
} from "lucide-react";

type BranchKey = "banter" | "reaffirmation" | "directive";

interface BranchCounts {
  banter: { last24h: number; prev24h: number };
  reaffirmation: { last24h: number; prev24h: number };
  directive: { last24h: number; prev24h: number };
}

const ZERO: BranchCounts = {
  banter: { last24h: 0, prev24h: 0 },
  reaffirmation: { last24h: 0, prev24h: 0 },
  directive: { last24h: 0, prev24h: 0 },
};

async function fetchBranchCounts(): Promise<BranchCounts> {
  const now = Date.now();
  const t24 = new Date(now - 24 * 3600_000).toISOString();
  const t48 = new Date(now - 48 * 3600_000).toISOString();

  // Helper: count rows com filtro temporal.
  // cortex_events usa `occurred_at`; as outras tabelas usam `created_at`.
  const TIME_COL: Record<string, string> = {
    cortex_events: "occurred_at",
    banter_detections: "created_at",
    admin_directives_log: "created_at",
  };
  const countWindow = async (
    table: "banter_detections" | "cortex_events" | "admin_directives_log",
    fromIso: string,
    toIso: string | null,
    extra?: (q: any) => any,
  ): Promise<number> => {
    const col = TIME_COL[table];
    let q: any = (supabase as any)
      .from(table)
      .select("id", { count: "exact", head: true })
      .gte(col, fromIso);
    if (toIso) q = q.lt(col, toIso);
    if (extra) q = extra(q);
    const { count, error } = await q;
    if (error) {
      console.warn(`[CortexBranchesPulse] count failed (${table}):`, error.message || JSON.stringify(error));
      return 0;
    }
    return count ?? 0;
  };

  const [
    banter24, banterPrev,
    reaff24, reaffPrev,
    dir24, dirPrev,
  ] = await Promise.all([
    countWindow("banter_detections", t24, null),
    countWindow("banter_detections", t48, t24),
    // Reaffirmation aparece como cortex_event.event_type='reaffirmation.blocked'
    countWindow("cortex_events", t24, null, (q) => q.eq("event_type", "reaffirmation.blocked")),
    countWindow("cortex_events", t48, t24, (q) => q.eq("event_type", "reaffirmation.blocked")),
    // Diretivas efetivamente aplicadas (não negadas, não erro)
    countWindow("admin_directives_log", t24, null, (q) =>
      q.in("applied_action", ["forget", "blacklist", "unblacklist", "confirmation_requested"]),
    ),
    countWindow("admin_directives_log", t48, t24, (q) =>
      q.in("applied_action", ["forget", "blacklist", "unblacklist", "confirmation_requested"]),
    ),
  ]);

  return {
    banter:        { last24h: banter24, prev24h: banterPrev },
    reaffirmation: { last24h: reaff24,  prev24h: reaffPrev },
    directive:     { last24h: dir24,    prev24h: dirPrev },
  };
}

function deltaPct(curr: number, prev: number): { pct: number; up: boolean } | null {
  if (curr === 0 && prev === 0) return null;
  if (prev === 0) return { pct: 100, up: true };
  const pct = Math.round(((curr - prev) / prev) * 100);
  return { pct: Math.abs(pct), up: pct >= 0 };
}

interface BranchTileProps {
  label: string;
  count: number;
  prev: number;
  icon: React.ElementType;
  to: string;
  description: string;
  isLoading: boolean;
}

function BranchTile({ label, count, prev, icon: Icon, to, description, isLoading }: BranchTileProps) {
  const delta = deltaPct(count, prev);
  const isCalm = count === 0;

  return (
    <div className="group relative rounded-xl border border-border/60 bg-card/50 hover:bg-card hover:border-border transition-all p-3.5">
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          isCalm ? "bg-muted/60 text-muted-foreground" : "bg-primary/10 text-primary"
        }`}>
          <Icon className="w-4 h-4" />
        </div>
        {!isLoading && delta && (
          <Badge
            variant="outline"
            className={`h-5 text-[10px] px-1.5 gap-0.5 font-medium tabular-nums ${
              delta.up && count > 0 ? "text-warning border-warning/30" : "text-success border-success/30"
            }`}
          >
            {delta.up
              ? <TrendingUp className="w-2.5 h-2.5" />
              : <TrendingDown className="w-2.5 h-2.5" />}
            {delta.pct}%
          </Badge>
        )}
        {!isLoading && !delta && (
          <Badge variant="outline" className="h-5 text-[10px] px-1.5 gap-0.5 text-muted-foreground border-border/60">
            <Minus className="w-2.5 h-2.5" />
            estável
          </Badge>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-8 w-12 mb-1" />
      ) : (
        <div className="flex items-baseline gap-1.5">
          <span className={`text-2xl font-bold font-headline tabular-nums ${
            isCalm ? "text-muted-foreground" : "text-foreground"
          }`}>{count}</span>
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide">24h</span>
        </div>
      )}

      <p className="text-xs font-medium text-foreground mt-1.5">{label}</p>
      <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 line-clamp-2">{description}</p>

      <Button
        asChild
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 px-1.5"
      >
        <Link to={to} aria-label={`Ver detalhes de ${label}`}>
          <ArrowRight className="w-3 h-3" />
        </Link>
      </Button>
    </div>
  );
}

export function CortexBranchesPulse() {
  const qc = useQueryClient();
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ["cortex-branches-pulse"],
    queryFn: fetchBranchCounts,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  // Realtime: invalida o cache quando nova row chega em qualquer das 3 tabelas.
  useEffect(() => {
    const channel = supabase
      .channel("cortex-branches-pulse")
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "banter_detections" },
        () => qc.invalidateQueries({ queryKey: ["cortex-branches-pulse"] }))
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "admin_directives_log" },
        () => qc.invalidateQueries({ queryKey: ["cortex-branches-pulse"] }))
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "cortex_events", filter: "event_type=eq.reaffirmation.blocked" },
        () => qc.invalidateQueries({ queryKey: ["cortex-branches-pulse"] }))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [qc]);

  const counts = data ?? ZERO;
  const total = counts.banter.last24h + counts.reaffirmation.last24h + counts.directive.last24h;
  const lastUpdated = useMemo(() => {
    if (!dataUpdatedAt) return null;
    const sec = Math.floor((Date.now() - dataUpdatedAt) / 1000);
    if (sec < 60) return `há ${sec}s`;
    return `há ${Math.floor(sec / 60)}min`;
  }, [dataUpdatedAt]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <Card className="border-border/50">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Cortex Hardening · branches
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] font-normal">
                {total} {total === 1 ? "evento" : "eventos"} · 24h
              </Badge>
              {lastUpdated && (
                <span className="text-[10px] text-muted-foreground tabular-nums hidden sm:inline">
                  atualizado {lastUpdated}
                </span>
              )}
              <span
                className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"
                title="Realtime conectado"
                aria-label="Realtime conectado"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <BranchTile
              label="Banter (zoeira)"
              count={counts.banter.last24h}
              prev={counts.banter.prev24h}
              icon={Laugh}
              to="/admin/health?tab=geral"
              description="Downgrades de intent factual → casual quando o grupo está claramente brincando."
              isLoading={isLoading}
            />
            <BranchTile
              label="Reaffirmation (loop)"
              count={counts.reaffirmation.last24h}
              prev={counts.reaffirmation.prev24h}
              icon={RotateCcw}
              to="/admin/health?tab=geral"
              description="Respostas bloqueadas por reafirmar fato já dito em janela curta."
              isLoading={isLoading}
            />
            <BranchTile
              label="Directives admin"
              count={counts.directive.last24h}
              prev={counts.directive.prev24h}
              icon={ShieldCheck}
              to="/admin/health?tab=sistema"
              description="Comandos de admin aplicados (esquece / blacklist / desbloqueia)."
              isLoading={isLoading}
            />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
